export type MaterialId = 'stone' | 'iron' | 'wood' | 'flesh';

export interface ModeSpec {
  readonly ratio: number;
  readonly q: number;
  readonly gain: number;
}

export interface MaterialSpec {
  readonly id: MaterialId;
  readonly name: string;
  readonly f0: number;
  readonly modes: readonly ModeSpec[];
  readonly transientHz: number;
  readonly transientAmount: number;
  readonly bodyAmount: number;
  readonly subAmount: number;
  readonly debrisAmount: number;
  readonly debrisGrains: number;
  readonly brightnessFloor: number;
  readonly brightnessCeiling: number;
}

export const STONE: MaterialSpec = {
  id: 'stone',
  name: 'Stone',
  f0: 210,
  modes: [
    { ratio: 1, q: 12, gain: 1 },
    { ratio: 1.83, q: 18, gain: 0.72 },
    { ratio: 3.09, q: 26, gain: 0.5 },
    { ratio: 4.4, q: 33, gain: 0.34 },
    { ratio: 5.71, q: 38, gain: 0.2 }
  ],
  transientHz: 2200,
  transientAmount: 1,
  bodyAmount: 0.85,
  subAmount: 0.9,
  debrisAmount: 1,
  debrisGrains: 14,
  brightnessFloor: 900,
  brightnessCeiling: 7000
};

export const IRON: MaterialSpec = {
  id: 'iron',
  name: 'Iron',
  f0: 420,
  modes: [
    { ratio: 1, q: 110, gain: 1 },
    { ratio: 2.01, q: 190, gain: 0.78 },
    { ratio: 3.9, q: 280, gain: 0.56 },
    { ratio: 14.37, q: 380, gain: 0.24 }
  ],
  transientHz: 3800,
  transientAmount: 1.15,
  bodyAmount: 1,
  subAmount: 0.55,
  debrisAmount: 0.45,
  debrisGrains: 7,
  brightnessFloor: 1400,
  brightnessCeiling: 12000
};

export const WOOD: MaterialSpec = {
  id: 'wood',
  name: 'Wood',
  f0: 180,
  modes: [
    { ratio: 1, q: 6, gain: 1 },
    { ratio: 2.78, q: 9, gain: 0.6 },
    { ratio: 7.38, q: 14, gain: 0.34 },
    { ratio: 15.38, q: 19, gain: 0.16 }
  ],
  transientHz: 1500,
  transientAmount: 0.9,
  bodyAmount: 0.95,
  subAmount: 0.5,
  debrisAmount: 0.8,
  debrisGrains: 11,
  brightnessFloor: 700,
  brightnessCeiling: 4800
};

export const FLESH: MaterialSpec = {
  id: 'flesh',
  name: 'Flesh',
  f0: 84,
  modes: [
    { ratio: 1, q: 1.2, gain: 1 },
    { ratio: 1.62, q: 1.8, gain: 0.55 },
    { ratio: 2.48, q: 2.8, gain: 0.28 },
    { ratio: 3.55, q: 3.8, gain: 0.12 }
  ],
  transientHz: 420,
  transientAmount: 0.45,
  bodyAmount: 0.7,
  subAmount: 1,
  debrisAmount: 0.1,
  debrisGrains: 4,
  brightnessFloor: 300,
  brightnessCeiling: 1800
};

export const MATERIALS: Readonly<Record<MaterialId, MaterialSpec>> = {
  stone: STONE,
  iron: IRON,
  wood: WOOD,
  flesh: FLESH
};

export const DEFAULT_MATERIAL: MaterialId = 'stone';

export interface ImpactTuning {
  readonly minSpeed: number;
  readonly refSpeed: number;
  readonly refMomentum: number;
  readonly transientExponent: number;
  readonly bodyExponent: number;
  readonly subExponent: number;
  readonly debrisExponent: number;
  readonly transientWeight: number;
  readonly bodyWeight: number;
  readonly subWeight: number;
  readonly debrisWeight: number;
  readonly brightnessExponent: number;
  readonly brightnessDecay: number;
  readonly brightnessDecaySeconds: number;
  readonly grazingFloor: number;
  readonly grazingExponent: number;
  readonly excitationAttack: number;
  readonly excitationRelease: number;
  readonly subStartHz: number;
  readonly subEndHz: number;
  readonly subSweepSeconds: number;
  readonly subDecaySeconds: number;
  readonly debrisSpreadSeconds: number;
  readonly debrisGrainSeconds: number;
  readonly maxVoices: number;
  readonly coalesceLimit: number;
  readonly cooldownSeconds: number;
  readonly layerFloor: number;
  readonly gainJitterDb: number;
  readonly bankCents: number;
  readonly modeCents: number;
  readonly modeGainDb: number;
  readonly brightnessJitter: number;
  readonly lookaheadSeconds: number;
  readonly masterGain: number;
  readonly softClipDrive: number;
  readonly softClipResolution: number;
  readonly maxVoiceSeconds: number;
  readonly noiseSeconds: number;
}

export const IMPACT_TUNING: ImpactTuning = {
  minSpeed: 0.3,
  refSpeed: 35,
  refMomentum: 320,
  transientExponent: 0.7,
  bodyExponent: 1.2,
  subExponent: 2.5,
  debrisExponent: 3.5,
  transientWeight: 0.6,
  bodyWeight: 0.85,
  subWeight: 0.55,
  debrisWeight: 0.25,
  brightnessExponent: 0.7,
  brightnessDecay: 0.55,
  brightnessDecaySeconds: 0.12,
  grazingFloor: 0.12,
  grazingExponent: 1.6,
  excitationAttack: 0.0012,
  excitationRelease: 0.0018,
  subStartHz: 110,
  subEndHz: 40,
  subSweepSeconds: 0.09,
  subDecaySeconds: 0.22,
  debrisSpreadSeconds: 0.26,
  debrisGrainSeconds: 0.006,
  maxVoices: 14,
  coalesceLimit: 4,
  cooldownSeconds: 0.05,
  layerFloor: 0.02,
  gainJitterDb: 2.5,
  bankCents: 60,
  modeCents: 25,
  modeGainDb: 4,
  brightnessJitter: 0.15,
  lookaheadSeconds: 0.02,
  masterGain: 0.32,
  softClipDrive: 1.7,
  softClipResolution: 2048,
  maxVoiceSeconds: 2.5,
  noiseSeconds: 2
};
